// atoms/QuestionImportQuestionList.js

import React from 'react';

const QuestionImportQuestionList = ({ 
  questions, 
  selectedQuestions, 
  onQuestionToggle,
  onSelectAll,
  onDeselectAll,
  onImport,
  isImporting 
}) => {

  const formatQuestionText = (text) => {
    if (!text) return 'No question text';
    return text;
  };

  const formatOptions = (options) => {
    if (!options) return [];
    
    try {
      // Try to parse as JSON first
      return JSON.parse(options);
    } catch (e) {
      // If JSON parsing fails, split by |||
      return options.split('|||').filter(option => option.trim() !== '');
    }
  };

  const getQuestionTypeLabel = (questionType) => {
    switch (questionType) {
      case 'Radio':
        return 'single choice';
      case 'Checkbox':
        return 'multiple choice';
      case 'LongAnswer':
        return 'essay';
      case 'Contradiction':
        return 'contradiction';
      default:
        return questionType?.toLowerCase() || 'unknown';
    }
  };

  return (
    <div>
      {/* Selection controls */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '12px 16px',
        backgroundColor: 'var(--color1-light)',
        borderRadius: '8px',
        border: '1px solid var(--border-color)'
      }}>
        <div style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center'
        }}>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: 'var(--color1)'
          }}>
            {selectedQuestions.size} of {questions.length} selected
          </span>
          <button
            onClick={onSelectAll}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: 'var(--color2-light)',
              color: 'var(--color2)',
              border: '1px solid var(--color2)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color2)';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color2-light)';
              e.target.style.color = 'var(--color2)';
            }}
          >
            Select All
          </button>
          <button
            onClick={onDeselectAll}
            style={{
              padding: '8px 16px',
              fontSize: '14px',
              backgroundColor: 'var(--color2-light)',
              color: 'var(--color2)',
              border: '1px solid var(--color2)',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--color2)';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--color2-light)';
              e.target.style.color = 'var(--color2)';
            }}
          >
            Deselect All
          </button>
          <button
            onClick={onImport}
            disabled={selectedQuestions.size === 0 || isImporting}
            style={{
              padding: '8px 16px',
              backgroundColor: selectedQuestions.size === 0 ? '#ccc' : 'var(--color1)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: selectedQuestions.size === 0 ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isImporting ? 'Importing...' : 'Import Selected'}
          </button>
        </div>
      </div>

      {/* Questions list */}
      <div style={{
        display: 'grid',
        gap: '16px'
      }}>
        {questions.map((question, index) => {
          const isSelected = selectedQuestions.has(question.id);
          const options = formatOptions(question.questionOptions);
          
          return (
            <div
              key={question.id}
              className="questionDesignBox"
              onClick={() => onQuestionToggle(question.id)}
              style={{
                border: isSelected ? '3px solid var(--color1)' : '1px solid var(--border-color)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backgroundColor: 'white',
                position: 'relative'
              }}
            >
              {/* Question type and ID header */}
              <div style={{ 
                marginBottom: '8px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center' 
              }}>
                <div style={{ display: 'flex' }}>
                  <span
                    className="radioButton selected"
                    style={{ 
                      marginRight: '8px',
                      pointerEvents: 'none' // Make it non-interactive
                    }}
                  >
                    {getQuestionTypeLabel(question.questionType)}
                  </span>
                </div>
                <div>
                  <span style={{ 
                    fontStyle: 'italic', 
                    fontSize: '0.9em', 
                    color: '#666' 
                  }}>
                    ID: {question.dataID || question.id}
                  </span>
                </div>
              </div>

              {/* Question text */}
              <div style={{ marginBottom: '8px' }}>
                <div
                  className="questionTextArea"
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    minHeight: '40px',
                    whiteSpace: 'pre-wrap',
                    pointerEvents: 'none'
                  }}
                >
                  {formatQuestionText(question.dataText)}
                </div>
              </div>

              {/* Options for Radio/Checkbox questions */}
              {(question.questionType === 'Radio' || question.questionType === 'Checkbox') && options.length > 0 && (
                <div style={{ marginBottom: '8px' }}>
                  {options.map((option, optionIndex) => (
                    <div key={optionIndex} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      marginBottom: '4px' 
                    }}>
                      <div
                        style={{
                          padding: '8px',
                          flex: '0.9',
                          maxWidth: '80%',
                          borderRadius: '4px',
                          fontFamily: 'var(--font-mono)',
                          fontSize: '14px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #e9ecef',
                          minHeight: '16px',
                          whiteSpace: 'pre-wrap',
                          marginLeft: 'calc(40px + 1.5em)', // Match the original spacing
                          pointerEvents: 'none'
                        }}
                      >
                        {option || `Option #${optionIndex + 1}`}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Logic display */}
              {question.questionLogic && question.questionLogic.trim() !== '' && (
                <div style={{ 
                  marginBottom: '8px', 
                  display: 'flex', 
                  alignItems: 'center' 
                }}>
                  <label style={{ 
                    marginRight: '8px', 
                    marginTop: '10px',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    logic:
                  </label>
                  <div
                    style={{
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      border: '1px solid #e9ecef',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '12px',
                      flex: 1,
                      whiteSpace: 'pre-wrap',
                      pointerEvents: 'none'
                    }}
                  >
                    {question.questionLogic}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuestionImportQuestionList;